import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  RotateCw
} from 'lucide-react';

interface Attachment {
  filename: string;
  size: number;
  content_type: string;
  s3_url?: string;
  s3_key?: string;
  uploaded_to_s3?: boolean;
}

interface AttachmentPreviewProps {
  attachments: Attachment[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachments,
  currentIndex,
  isOpen,
  onClose,
  onIndexChange,
}) => {
  const [imageScale, setImageScale] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);

  const currentAttachment = attachments[currentIndex];

  // 重置缩放和旋转
  const resetImageTransform = useCallback(() => {
    setImageScale(1);
    setImageRotation(0);
  }, []);

  // 切换到上一个附件
  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : attachments.length - 1;
    onIndexChange(newIndex);
    resetImageTransform();
  }, [currentIndex, attachments.length, onIndexChange, resetImageTransform]);

  // 切换到下一个附件
  const goToNext = useCallback(() => {
    const newIndex = currentIndex < attachments.length - 1 ? currentIndex + 1 : 0;
    onIndexChange(newIndex);
    resetImageTransform();
  }, [currentIndex, attachments.length, onIndexChange, resetImageTransform]);

  // 缩放控制
  const zoomIn = () => setImageScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setImageScale(prev => Math.max(prev - 0.25, 0.25));
  const rotate = () => setImageRotation(prev => (prev + 90) % 360);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          rotate();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevious, goToNext, onClose]);

  // 下载附件
  const downloadAttachment = (attachment: Attachment) => {
    if (attachment.s3_url && attachment.uploaded_to_s3) {
      const link = document.createElement('a');
      link.href = attachment.s3_url;
      link.download = attachment.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // 判断文件类型
  const isImage = (contentType: string) => contentType.startsWith('image/');
  const isVideo = (contentType: string) => contentType.startsWith('video/');
  const isAudio = (contentType: string) => contentType.startsWith('audio/');
  const isPDF = (contentType: string) => contentType.includes('pdf');
  const isText = (contentType: string) => contentType.startsWith('text/');

  // 渲染预览内容
  const renderPreviewContent = () => {
    if (!currentAttachment || !currentAttachment.s3_url) {
      return (
        <div className="flex items-center justify-center h-full text-white">
          <div className="text-center">
            <div className="text-6xl mb-4">📄</div>
            <div className="text-xl mb-2">附件不可用</div>
            <div className="text-gray-300">附件可能正在处理中或上传失败</div>
          </div>
        </div>
      );
    }

    const { content_type, s3_url, filename } = currentAttachment;

    if (isImage(content_type)) {
      return (
        <div className="flex items-center justify-center h-full">
          <img
            src={s3_url}
            alt={filename}
            className="max-w-full max-h-full object-contain transition-transform duration-200"
            style={{
              transform: `scale(${imageScale}) rotate(${imageRotation}deg)`,
            }}
            onError={(e) => {
              console.error('图片加载失败:', filename);
            }}
          />
        </div>
      );
    }

    if (isVideo(content_type)) {
      return (
        <div className="flex items-center justify-center h-full">
          <video
            src={s3_url}
            controls
            className="max-w-full max-h-full"
            style={{ maxHeight: '80vh' }}
          >
            您的浏览器不支持视频播放
          </video>
        </div>
      );
    }

    if (isAudio(content_type)) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-6xl mb-6">🎵</div>
            <audio src={s3_url} controls className="mb-4">
              您的浏览器不支持音频播放
            </audio>
            <div className="text-white text-lg">{filename}</div>
          </div>
        </div>
      );
    }

    if (isPDF(content_type)) {
      return (
        <div className="h-full">
          <iframe
            src={s3_url}
            className="w-full h-full border-0"
            title={filename}
          />
        </div>
      );
    }

    if (isText(content_type)) {
      return (
        <div className="flex items-center justify-center h-full">
          <iframe
            src={s3_url}
            className="w-full h-4/5 bg-white rounded"
            title={filename}
          />
        </div>
      );
    }

    // 不支持预览的文件类型
    return (
      <div className="flex items-center justify-center h-full text-white">
        <div className="text-center">
          <div className="text-6xl mb-4">📄</div>
          <div className="text-xl mb-2">不支持预览</div>
          <div className="text-gray-300 mb-4">此文件类型不支持在线预览</div>
          <button
            onClick={() => downloadAttachment(currentAttachment)}
            className="px-4 py-2 theme-blue-button rounded-lg theme-transition flex items-center gap-2 mx-auto"
          >
            <Download size={16} />
            下载文件
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex flex-col">
      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between p-4 bg-black bg-opacity-50">
        <div className="flex items-center space-x-4 text-white">
          <h3 className="text-lg font-medium truncate max-w-md">
            {currentAttachment?.filename || '未知文件'}
          </h3>
          <span className="text-sm text-gray-300">
            {formatFileSize(currentAttachment?.size || 0)}
          </span>
          <span className="text-sm text-gray-300">
            {currentIndex + 1} / {attachments.length}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          {/* 图片控制按钮 */}
          {currentAttachment && isImage(currentAttachment.content_type) && (
            <>
              <button
                onClick={zoomOut}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded theme-transition"
                title="缩小 (-)"
              >
                <ZoomOut size={20} />
              </button>
              <button
                onClick={zoomIn}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded theme-transition"
                title="放大 (+)"
              >
                <ZoomIn size={20} />
              </button>
              <button
                onClick={rotate}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded theme-transition"
                title="旋转 (R)"
              >
                <RotateCw size={20} />
              </button>
              <button
                onClick={resetImageTransform}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded theme-transition text-sm"
                title="重置"
              >
                1:1
              </button>
            </>
          )}

          {/* 下载按钮 */}
          {currentAttachment && currentAttachment.s3_url && (
            <button
              onClick={() => downloadAttachment(currentAttachment)}
              className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded theme-transition"
              title="下载"
            >
              <Download size={20} />
            </button>
          )}

          {/* 关闭按钮 */}
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded theme-transition"
            title="关闭 (Esc)"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* 主预览区域 */}
      <div className="flex-1 relative overflow-hidden">
        {/* 预览内容 */}
        <div className="absolute inset-0">
          {renderPreviewContent()}
        </div>

        {/* 左右切换按钮 */}
        {attachments.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full theme-transition z-10"
              title="上一个 (←)"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full theme-transition z-10"
              title="下一个 (→)"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* 底部缩略图导航 */}
      {attachments.length > 1 && (
        <div className="p-4 bg-black bg-opacity-50">
          <div className="flex justify-center space-x-2 overflow-x-auto max-w-full">
            {attachments.map((attachment, index) => (
              <button
                key={index}
                onClick={() => {
                  onIndexChange(index);
                  resetImageTransform();
                }}
                className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden theme-transition ${
                  index === currentIndex
                    ? 'border-blue-500 opacity-100'
                    : 'border-gray-500 opacity-60 hover:opacity-80'
                }`}
              >
                {attachment.content_type.startsWith('image/') && attachment.s3_url ? (
                  <img
                    src={attachment.s3_url}
                    alt={attachment.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white text-xs">
                    {attachment.filename.split('.').pop()?.toUpperCase() || '?'}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 键盘提示 */}
      <div className="absolute bottom-4 left-4 text-white text-xs bg-black bg-opacity-50 rounded px-2 py-1">
        ESC: 关闭 | ←→: 切换 | +/-: 缩放 | R: 旋转
      </div>
    </div>
  );
};

export default AttachmentPreview; 